import { IMAGE_VARIANTS } from "@/lib/images";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, Phone, ArrowRight } from "lucide-react";
import QuoteDialog from "@/components/QuoteDialog";
import api from "@/lib/api";

const INDUSTRIES = [
  { slug: "commercial-printing", name: "Commercial Printing" },
  { slug: "packaging", name: "Packaging" },
  { slug: "labels", name: "Label Manufacturing" },
  { slug: "non-woven-bag", name: "Non Woven Bag Manufacturing" },
  { slug: "paper-bag", name: "Paper Bag Manufacturing" },
  { slug: "converting", name: "Flexible Packaging & Converting" },
];

const BRANDS = [
  { slug: "heidelberg", name: "Heidelberg" },
  { slug: "bobst", name: "Bobst" },
  { slug: "komori", name: "Komori" },
  { slug: "koenig-bauer", name: "Koenig & Bauer" },
  { slug: "manroland", name: "manroland" },
  { slug: "mark-andy", name: "Mark Andy" },
  { slug: "siemens", name: "Siemens" },
  { slug: "abb", name: "ABB" },
];

const CATEGORIES = [
  "Combination Hybrid Printing Machinery",
  "Four Colour Sheet Fed Offset Printing Machine",
  "Flexographic Printing Machinery",
  "Paper Bag Making Machinery and Allied Machinery",
  "Paper Bag Making Machinery with Printing",
  "Non Woven Bag Making Machinery and Allied Machinery",
  "Smart Factory Automation",
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && (setOpenMega(null), setSearchOpen(false));
    const onClick = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener("keydown", onEsc);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onEsc); document.removeEventListener("mousedown", onClick); };
  }, []);

  // Debounced autosuggest
  useEffect(() => {
    if (!search.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/products", { params: { search, limit: 6 } });
        setResults(r.data);
      } catch { setResults([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/products?search=${encodeURIComponent(search)}`);
      setSearch(""); setSearchOpen(false); setResults(null);
    }
  };

  // Group results into categorised buckets by brand + industry
  const bucketed = (() => {
    if (!results) return null;
    const brands = new Set();
    const industries = new Set();
    results.forEach((p) => {
      if (p.brand && p.brand.toLowerCase().includes(search.toLowerCase())) brands.add(p.brand);
      (p.industry || []).forEach((i) => { if (i.includes(search.toLowerCase())) industries.add(i); });
    });
    return { products: results, brands: [...brands], industries: [...industries] };
  })();

  const megaItems = [
    { key: "products", label: "Products" },
    { key: "industries", label: "Industries" },
  ];

  return (
    <>
      <div className="bg-[#0F3D66] text-white text-xs" data-testid="topbar">
        <div className="gms-container flex items-center justify-between h-10">
          <span className="tracking-wide opacity-90">Precision. Performance. Partnership.</span>
          <div className="hidden md:flex items-center gap-5">
            <a href="tel:+919810012256" className="flex items-center gap-2 hover:text-white/80" data-testid="topbar-phone">
              <Phone className="w-3.5 h-3.5" strokeWidth={1.75} /> +91 98100 12256
            </a>
            <a href="mailto:info@graphicmachinerysolutions.com" className="hover:text-white/80" data-testid="topbar-email">
              info@graphicmachinerysolutions.com
            </a>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0]" data-testid="navbar">
        <div className="gms-container flex items-center gap-8 h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="w-10 h-10 bg-[#0F3D66] flex items-center justify-center">
              <span className="text-white font-heading font-extrabold text-lg leading-none">G</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-[#0F3D66] text-lg tracking-tight">GMS</div>
              <div className="text-[10px] uppercase tracking-widest text-[#5F6B76]">Graphic Machinery</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 relative" onMouseLeave={() => setOpenMega(null)}>
            {/* Products mega */}
            <button
              onMouseEnter={() => setOpenMega("products")}
              onClick={() => setOpenMega(openMega === "products" ? null : "products")}
              data-testid="nav-products"
              className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-[#0F3D66] hover:text-[#007BFF] transition-colors"
            >
              Products <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
            <Link to="/solutions" onMouseEnter={() => setOpenMega(null)} className="px-4 py-2 text-sm font-semibold text-[#0F3D66] hover:text-[#007BFF]" data-testid="nav-solutions">Solutions</Link>
            {/* Industries mega */}
            <button
              onMouseEnter={() => setOpenMega("industries")}
              onClick={() => setOpenMega(openMega === "industries" ? null : "industries")}
              data-testid="nav-industries"
              className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-[#0F3D66] hover:text-[#007BFF] transition-colors"
            >
              Industries <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
            <Link to="/services" onMouseEnter={() => setOpenMega(null)} className="px-4 py-2 text-sm font-semibold text-[#0F3D66] hover:text-[#007BFF]" data-testid="nav-services">Services</Link>
            <Link to="/about" onMouseEnter={() => setOpenMega(null)} className="whitespace-nowrap px-4 py-2 text-sm font-semibold text-[#0F3D66] hover:text-[#007BFF]" data-testid="nav-about">About Us</Link>
            <Link to="/contact" onMouseEnter={() => setOpenMega(null)} className="px-4 py-2 text-sm font-semibold text-[#0F3D66] hover:text-[#007BFF]" data-testid="nav-contact">Contact</Link>

            {openMega && (
              <div className="absolute top-full left-0 w-[820px] bg-white border border-[#E2E8F0] shadow-lg mt-0 p-8 grid grid-cols-3 gap-8" data-testid={`mega-${openMega}`}>
                {openMega === "products" && (
                  <>
                    <div className="col-span-2">
                      <div className="gms-eyebrow mb-3">Categories</div>
                      <ul className="space-y-2">
                        {CATEGORIES.map((c) => (
                          <li key={c}><Link to={`/products?category=${encodeURIComponent(c)}`} className="text-sm text-[#5F6B76] hover:text-[#007BFF]">{c}</Link></li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-[#F5F7FA] p-5 border border-[#E2E8F0]">
                      <div className="gms-eyebrow mb-2">Not sure?</div>
                      <div className="font-heading font-bold text-[#0F3D66] text-lg mb-3">Machine Finder</div>
                      <p className="text-sm text-[#5F6B76] mb-4">Answer three questions. Get matched machines from our catalogue.</p>
                      <Link to="/machine-finder" className="gms-btn-primary text-sm" data-testid="mega-cta-finder">Launch</Link>
                    </div>
                  </>
                )}
                {openMega === "industries" && (
                  <div className="col-span-3 grid grid-cols-3 gap-6">
                    {INDUSTRIES.map((i) => (
                      <Link key={i.slug} to={`/products?industry=${i.slug}`} className="p-4 border border-[#E2E8F0] hover:border-[#007BFF] transition-colors" data-testid={`mega-industry-${i.slug}`}>
                        <div className="font-heading font-semibold text-[#0F3D66]">{i.name}</div>
                      </Link>
                    ))}
                  </div>
                )}
                {openMega === "brands" && (
                  <div className="col-span-3 grid grid-cols-4 gap-4">
                    {BRANDS.map((b) => (
                      <Link key={b.slug} to={`/products?brand=${b.slug}`} className="p-4 border border-[#E2E8F0] hover:border-[#007BFF] transition-colors text-center" data-testid={`mega-brand-${b.slug}`}>
                        <div className="font-heading font-semibold text-[#0F3D66]">{b.name}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="hidden md:flex flex-1 justify-end items-center gap-3">
            <div ref={searchRef} className="relative">
              <form onSubmit={handleSearch}>
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5F6B76] pointer-events-none" strokeWidth={1.75} />
                <input
                  data-testid="nav-search-input"
                  type="text"
                  value={search}
                  onFocus={() => setSearchOpen(true)}
                  onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
                  placeholder="Search machines, brands, specs…"
                  className="pl-9 pr-3 py-2 w-56 lg:w-72 bg-[#F5F7FA] border border-transparent focus:border-[#007BFF] focus:bg-white focus:outline-none rounded-sm text-sm"
                />
              </form>
              {searchOpen && search.trim() && (
                <div className="absolute top-full right-0 mt-2 w-[420px] bg-white border border-[#E2E8F0] shadow-lg z-50" data-testid="search-suggest">
                  {results === null && <div className="p-4 text-xs text-[#5F6B76]">Searching…</div>}
                  {results && results.length === 0 && <div className="p-4 text-xs text-[#5F6B76]">No matches. Try a broader term.</div>}
                  {bucketed && bucketed.products.length > 0 && (
                    <div className="py-2">
                      <div className="px-4 pt-2 pb-1 gms-eyebrow">Machines</div>
                      {bucketed.products.map((p) => (
                        <Link
                          key={p.slug}
                          to={`/products/${p.slug}`}
                          onClick={() => { setSearchOpen(false); setSearch(""); }}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-[#F5F7FA]"
                          data-testid={`suggest-product-${p.slug}`}
                        >
                          <img src={IMAGE_VARIANTS[p.images?.[0]]?.srcSet.split(" ")[0] || p.images?.[0]} alt="" loading="lazy" width="40" height="40" className="w-10 h-10 object-cover border border-[#E2E8F0]" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-[#0F3D66] truncate">{p.name}</div>
                            <div className="text-[11px] text-[#5F6B76] truncate">{p.brand} · {p.category}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {bucketed && bucketed.brands.length > 0 && (
                    <div className="border-t border-[#E2E8F0] py-2">
                      <div className="px-4 pt-2 pb-1 gms-eyebrow">Brands</div>
                      {bucketed.brands.map((b) => (
                        <Link key={b} to={`/products?brand=${b.toLowerCase()}`} onClick={()=>{setSearchOpen(false); setSearch("");}} className="block px-4 py-1.5 text-sm text-[#5F6B76] hover:text-[#007BFF]" data-testid={`suggest-brand-${b}`}>{b}</Link>
                      ))}
                    </div>
                  )}
                  {bucketed && bucketed.industries.length > 0 && (
                    <div className="border-t border-[#E2E8F0] py-2">
                      <div className="px-4 pt-2 pb-1 gms-eyebrow">Industries</div>
                      {bucketed.industries.map((i) => (
                        <Link key={i} to={`/products?industry=${i}`} onClick={()=>{setSearchOpen(false); setSearch("");}} className="block px-4 py-1.5 text-sm text-[#5F6B76] hover:text-[#007BFF] capitalize" data-testid={`suggest-industry-${i}`}>{i.replace(/-/g," ")}</Link>
                      ))}
                    </div>
                  )}
                  {results && results.length > 0 && (
                    <button onClick={handleSearch} className="w-full text-left border-t border-[#E2E8F0] px-4 py-2.5 text-xs font-semibold text-[#007BFF] hover:bg-[#F5F7FA] flex items-center gap-1" data-testid="suggest-see-all">
                      See all results for "{search}" <ArrowRight className="w-3 h-3"/>
                    </button>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setQuoteOpen(true)} className="gms-btn-primary text-sm whitespace-nowrap" data-testid="nav-request-quote">Request a Quote</button>
          </div>

          <button className="lg:hidden ml-auto" onClick={() => setMobileOpen((s) => !s)} data-testid="nav-mobile-toggle" aria-label="menu">
            {mobileOpen ? <X className="w-6 h-6 text-[#0F3D66]" /> : <Menu className="w-6 h-6 text-[#0F3D66]" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-[#E2E8F0] bg-white" data-testid="mobile-nav">
            <div className="gms-container py-4 flex flex-col gap-1">
              <form onSubmit={(e)=>{handleSearch(e); setMobileOpen(false);}} className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5F6B76] pointer-events-none" strokeWidth={1.75} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search machines, brands, specs…"
                  className="w-full pl-9 pr-3 py-2.5 bg-[#F5F7FA] border border-transparent focus:border-[#007BFF] focus:bg-white focus:outline-none rounded-sm text-sm"
                  data-testid="mobile-search-input"
                  aria-label="Search"
                />
              </form>
              {[["/products","Products"],["/solutions","Solutions"],["/industries","Industries"],["/services","Services"],["/about","About Us"],["/contact","Contact"],["/machine-finder","Machine Finder"],["/faq","FAQ"]].map(([to,label]) => (
                <Link key={to} to={to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${to.replace("/","")}`} className="py-3 text-sm font-semibold text-[#0F3D66] border-b border-[#E2E8F0]">{label}</Link>
              ))}
              <button onClick={() => { setQuoteOpen(true); setMobileOpen(false); }} className="gms-btn-primary text-sm mt-3" data-testid="mobile-request-quote">Request a Quote</button>
            </div>
          </div>
        )}
      </header>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </>
  );
}
